import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Image, Spin, Typography } from 'antd';

const { Text } = Typography;

const ModuleThumbnail = React.memo(({ entry, isSelected, onClick }) => {
    const { moduleName, imagePath, displayName } = entry;
    const baseImagePath = imagePath;

    const [currentImgSrc, setCurrentImgSrc] = useState(baseImagePath);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setCurrentImgSrc(baseImagePath);
        setIsLoading(true);
        setHasError(false);
    }, [baseImagePath]);

    const handleImageError = () => {
        setIsLoading(false);
        setHasError(true);
    };

    const handleImageLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const placeholder = (
        <div style={{ width: '100%', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#555'}}>
            {isLoading && !hasError && <Spin size="small"/>}
            {hasError && <Text style={{color: '#888', fontSize: '0.5rem'}}>No Preview</Text>}
        </div>
    );

    const imageStyle = {
         objectFit: 'cover',
         marginBottom: '4px',
         display: 'block',
         width: '100%',
         height: 60
    };

    const containerStyle = {
        cursor: 'pointer',
        border: isSelected ? '2px solid #1677ff' : '2px solid #444',
        borderRadius: '4px',
        padding: '4px',
        textAlign: 'center',
        backgroundColor: isSelected ? '#3a4a5f' : '#2a3038',
        transition: 'border-color 0.2s, background-color 0.2s',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    };

    return (
        <div
            onClick={onClick}
            style={containerStyle}
            title={`Focus on ${moduleName.replace(/_/g, ' ')}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
        >
            <Image
                width="100%"
                height={60}
                src={currentImgSrc}
                alt={hasError ? `Error loading image for ${displayName}` : `Preview of ${displayName}`}
                preview={false}
                placeholder={placeholder}
                style={imageStyle}
                onLoad={handleImageLoad}
                onError={handleImageError}
                fallback="/path/to/generic-placeholder-image.png"
            />
            <Text ellipsis style={{ marginTop: '3px',color: '#eee', fontSize: '0.7rem', lineHeight: '1.5', display: 'block' }}>
                {displayName}
            </Text>
        </div>
    );
});

ModuleThumbnail.displayName = 'ModuleThumbnail';

ModuleThumbnail.propTypes = {
    entry: PropTypes.shape({
        moduleName: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        category: PropTypes.string,
        imagePath: PropTypes.string.isRequired,
    }).isRequired,
    isSelected: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
};

export default ModuleThumbnail;